import React from 'react';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  NormalizedCacheObject,
  split,
  HttpLink,
  gql,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useMaintenanceMode } from '@quality24/react-maintenance-mode';
import i18n from 'i18next';

import { AuthPayload } from '../../@types/auth';
import {
  DEFAULT_LANGUAGE,
  toAcceptLanguage,
} from '@/inpatient-patient-pwa/i18n/constants';

import { headers as projectHeaders } from '../../services/http';
import subscriptionClient from '../../services/websocket';

import { logout } from '../../services/auth';
import {
  cache,
  retryRefreshToken,
  setMaintenanceModeListener,
} from '../../services/graphql';
import { getIdToken, getRefreshToken } from '../../services/storage';
import { TokenRefreshLink } from './TokenRefreshLink';

// //////////////////////////////////////////////
// Types
// //////////////////////////////////////////////

export interface Props {
  children: React.ReactNode;
  apiURL: string;
}

/**
 * Stores the promise of a refresh operation.
 * Note: store it globally in order not to run multiple
 * refreshes at the same time
 */

let refreshingPromise: Promise<AuthPayload | void | null> | null = null;

// //////////////////////////////////////////////
// Custom fetch definition
// //////////////////////////////////////////////

/**
 * This fetch is passed to the BatchHttpLink (apollo link)
 * for handling re-authorization.
 */
const fetcher = (uri: RequestInfo | URL, options: RequestInit | undefined) =>
  fetch(uri, {
    ...options,
    headers: { ...options?.headers, ...projectHeaders },
  }).then((response) => response);

// //////////////////////////////////////////////
// Link definitions
// //////////////////////////////////////////////

// Create the HTTP auth middleware
//
// Merges into whatever headers earlier links already put on the context (the
// locale chain, notably) instead of replacing them — `setContext` returns the
// *context*, so assigning a fresh `headers` object here drops everything set
// upstream.
export const authLink = setContext((_ignore, { headers }) => {
  const token = getIdToken();
  if (!token) return { headers };

  return {
    headers: { ...headers, authorization: `Bearer ${token}` },
  };
});

// Adds the content locale chain as the Accept-Language header so the API can
// resolve `*I18N` fields. Read synchronously from the i18next singleton.
//
// The whole chain travels, not just the active language, so the fallback order
// is stated on the wire instead of relying on the API's hard-coded one.
export const localeLink = setContext((_ignore, { headers }) => ({
  headers: {
    ...headers,
    'Accept-Language': toAcceptLanguage(i18n.language || DEFAULT_LANGUAGE),
  },
}));

// Create WebSocket link
export const wsLink = new GraphQLWsLink(subscriptionClient);

const ExtendedApolloProvider: React.FunctionComponent<Props> = ({
  children,
  apiURL,
}) => {
  const { enableMaintenanceMode, isEnabled: isMaintenanceModeEnabled } =
    useMaintenanceMode();

  // Show the maintenance screen when a token refresh hits the maintenance mode
  React.useEffect(
    () => setMaintenanceModeListener(enableMaintenanceMode),
    [enableMaintenanceMode],
  );

  // Create an Batch HTTP link
  const httpLink = React.useMemo(
    () =>
      new HttpLink({
        uri: apiURL,
        fetch: fetcher,
      }),
    [apiURL],
  );

  // Create the refresh token link
  const refreshTokenLink = React.useMemo(
    () => new TokenRefreshLink({ apiUrl: apiURL }),
    [apiURL],
  );

  const errorLink = React.useMemo(
    () =>
      onError(({ graphQLErrors, networkError, forward, operation }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach((error) => {
            if (error && 'statusCode' in error) {
              // Forbidden graphQL error inside Apollo response. Happens when authentication is not valid.
              if (error.statusCode === 403) {
                logout();
                return;
              }

              // Unauthorized graphQL error inside Apollo response. Happens on expired token.
              if (error.statusCode === 401) {
                // Refresh token if not yet running an operation
                if (!refreshingPromise && apiURL) {
                  const refreshToken = getRefreshToken() as string;
                  refreshingPromise = retryRefreshToken(apiURL, refreshToken);

                  // Retry current operation on promise resolved (run after default handlers)
                  refreshingPromise.then(
                    (authPayload: void | AuthPayload | null) => {
                      if (!authPayload) return;

                      // Retry previous operation
                      const prevContext = operation.getContext();
                      const newContext = {
                        ...prevContext,
                        headers: {
                          ...prevContext.headers,
                          authorization: `Bearer ${authPayload.idToken}`,
                        },
                      };

                      operation.setContext(newContext);

                      forward(operation);
                    },
                  );
                }
              }
            }
          });
        }

        if (networkError && 'statusCode' in networkError) {
          // Forbidden graphQL error inside Apollo response. Happens when authentication is not valid.
          if (networkError.statusCode === 403) {
            logout();
            return;
          }
          // Unauthorized graphQL error inside Apollo response. Happens on expired token.
          if (networkError.statusCode === 401) {
            // Refresh token if not yet running an operation
            if (!refreshingPromise) {
              const refreshToken = getRefreshToken() as string;
              refreshingPromise = retryRefreshToken(apiURL, refreshToken);

              // Retry current operation on promise resolved (run after default handlers)
              refreshingPromise.then(
                (authPayload: void | AuthPayload | null) => {
                  if (!authPayload) return;

                  // Retry previous operation
                  const prevContext = operation.getContext();
                  const newContext = {
                    ...prevContext,
                    headers: {
                      ...prevContext.headers,
                      authorization: `Bearer ${authPayload.idToken}`,
                    },
                  };

                  operation.setContext(newContext);

                  forward(operation);
                },
              );
            }
          }
          // maintenance mode
          if (
            networkError.statusCode === 503 &&
            networkError.response.headers.has('Retry-After')
          ) {
            enableMaintenanceMode();
          }
        }
      }),
    [enableMaintenanceMode, apiURL],
  );

  const client: ApolloClient<NormalizedCacheObject> = React.useMemo(
    () =>
      new ApolloClient({
        link: split(
          // Split based on operation type
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === 'OperationDefinition' &&
              definition.operation === 'subscription'
            );
          },
          wsLink,
          ApolloLink.from([
            errorLink,
            refreshTokenLink,
            localeLink,
            authLink,
            httpLink,
          ]),
        ),
        cache,

        // Set local storage resolvers
        defaultOptions: {
          watchQuery: { fetchPolicy: 'cache-and-network' },
        },
      }),
    [errorLink, httpLink, refreshTokenLink],
  );

  React.useEffect(() => {
    /**
     * Invalidate the cache whenever the language changes so queries refetch
     * with the new Accept-Language header (the `*I18N` fields are resolved
     * server-side and are not keyed by query variables).
     */
    const handleLanguageChanged = () => {
      client.resetStore().catch(() => {
        // resetStore rejects if there are no active queries — safe to ignore.
      });
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [client]);

  React.useEffect(() => {
    /**
     * Attach connection listeners
     */
    const unsubscribeConnectedListener = subscriptionClient.on(
      'connected',
      () => {
        // Reset apollo store to refetch all active queries. This side-effect is suggested in
        // https://github.com/apollographql/apollo-client/issues/1831#issuecomment-311442700
        client.reFetchObservableQueries();
        // update...
      },
    );

    const unsubscribeErrorListener = subscriptionClient.on('error', () => {
      console.info('WebSocket error.'); // eslint-disable-line no-console
      if (!isMaintenanceModeEnabled) {
        client
          .query({
            query: gql`
              query FetchApiVersion {
                apiVersion {
                  versionName
                }
              }
            `,
          })
          .catch((error) => {
            if (
              error?.networkError?.statusCode === 503 &&
              error?.networkError?.response?.headers.get('Retry-After')
            ) {
              enableMaintenanceMode();
            }
          });
      }
    });

    return () => {
      unsubscribeErrorListener();
      unsubscribeConnectedListener();
    };
  }, [apiURL, client, enableMaintenanceMode, isMaintenanceModeEnabled]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ExtendedApolloProvider;
